import { apiFetch } from './client';

export type RingStrategy = 'simultaneous' | 'sequential' | 'round_robin' | 'random';

export interface RingGroupRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  ref_number: string;
  name: string;
  strategy: RingStrategy;
  ring_timeout_sec: number;
  fallback_node_id: string | null;
  no_answer_fallback_kind: string | null;
  no_answer_fallback_target: string | null;
  active: boolean;
  /** Optional FK to contacts(id) — internal-CID source applied to every
   *  member of the group at fan-out time (migration 0085). */
  internal_cid_contact_id: string | null;
  /** Freeform fallback when no contact is linked (migration 0085). */
  internal_cid_override: string | null;
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface MemberRow {
  id: string;
  ring_group_id: string;
  member_kind: 'phone' | 'extension';
  member_value: string;
  ring_order: number;
  ring_timeout_sec: number | null;
  active: boolean;
  created_at: string;
}

export interface CreateGroupBody {
  name: string;
  strategy?: RingStrategy;
  ring_timeout_sec?: number;
  fallback_node_id?: string | null;
  no_answer_fallback_kind?: 'voicemail' | 'node' | 'external' | null;
  no_answer_fallback_target?: string | null;
  customer_id?: string;
  /** Migration 0085 — link a contact / set freeform override. */
  internal_cid_contact_id?: string | null;
  internal_cid_override?: string | null;
}

export type PatchGroupBody = Partial<Omit<CreateGroupBody, 'customer_id'>>;

export interface AddMemberBody {
  member_kind: 'phone' | 'extension';
  member_value: string;
  ring_order?: number;
  ring_timeout_sec?: number | null;
}

export const ringGroups = {
  list: (p: { search?: string; limit?: number; customer_id?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.search) q.set('search', p.search);
    if (p.limit) q.set('limit', String(p.limit));
    if (p.customer_id) q.set('customer_id', p.customer_id);
    return apiFetch<{ rows: RingGroupRow[] }>(`/api/ring-groups?${q}`);
  },
  get: (id: string) => apiFetch<RingGroupRow>(`/api/ring-groups/${id}`),
  create: (body: CreateGroupBody) =>
    apiFetch<RingGroupRow>('/api/ring-groups', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, patch: PatchGroupBody) =>
    apiFetch<RingGroupRow>(`/api/ring-groups/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id: string) => apiFetch<void>(`/api/ring-groups/${id}`, { method: 'DELETE' }),
  listMembers: (groupId: string) =>
    apiFetch<{ rows: MemberRow[] }>(`/api/ring-groups/${groupId}/members`),
  addMember: (groupId: string, body: AddMemberBody) =>
    apiFetch<MemberRow>(`/api/ring-groups/${groupId}/members`, { method: 'POST', body: JSON.stringify(body) }),
  removeMember: (groupId: string, memberId: string) =>
    apiFetch<void>(`/api/ring-groups/${groupId}/members/${memberId}`, { method: 'DELETE' }),
};
